import { motion } from "framer-motion";
import { X } from "lucide-react";

/**
 * Modal container. Handles backdrop, ESC/outside close, entrance animation.
 * Children are rendered inside a lux-card.
 * Usage:
 *   <NexaModal open={open} onClose={...} title="New invoice" subtitle="…">
 *     <form>...</form>
 *   </NexaModal>
 */
export function NexaModal({ open, onClose, title, subtitle, children, size = "lg", testId }) {
  if (!open) return null;
  const maxW = { md: "max-w-md", lg: "max-w-xl", xl: "max-w-2xl", "2xl": "max-w-3xl" }[size] || "max-w-xl";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-nexa-accent/40 backdrop-blur-[2px] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 8, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3, ease: [0.2, 0.7, 0.2, 1] }}
        onClick={(e) => e.stopPropagation()}
        className={`lux-card w-full ${maxW} p-6 relative max-h-[85vh] overflow-y-auto`}
        data-testid={testId}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-nexa-mute hover:text-nexa-ink transition-colors"
          aria-label="Close"
          data-testid={testId ? `${testId}-close` : undefined}
        >
          <X className="w-4 h-4" strokeWidth={1.7} />
        </button>
        {title && <h2 className="text-[17px] font-semibold text-nexa-ink pr-6">{title}</h2>}
        {subtitle && <p className="mt-1 text-[12.5px] text-nexa-mute">{subtitle}</p>}
        <div className={title ? "mt-4" : ""}>{children}</div>
      </motion.div>
    </motion.div>
  );
}
